import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { paidApi, paidError, readSignupToken, saveSignupToken, validPlan, readPlan, savePlan, startPaidGoogle } from './paidSignupApi';

export const usePaidSignup = () => {
  const location = useLocation(), navigate = useNavigate();
  const auth = useAuth();
  const initial = useRef(false), gate = useRef(false);
  const [token, setToken] = useState(readSignupToken);
  const [step, setStep] = useState('account');
  const [form, setForm] = useState({ name: '', email: '', password: '', business: '' });
  const [provider, setProvider] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState(() => validPlan(new URLSearchParams(location.search).get('plan')) || readPlan());
  const [payment, setPayment] = useState(null);
  const [freeActivated, setFreeActivated] = useState(false);
  const [busy, setBusy] = useState(false), [restoring, setRestoring] = useState(true);
  const [error, setError] = useState(() => location.state?.paidSignupError || '');
  const plan = plans.find(p => p.id === planId);
  useLayoutEffect(() => {
    document.documentElement.classList.add('signup-review-viewport');
    const title = document.title; document.title = 'Get started — Voxinity AI';
    return () => { document.documentElement.classList.remove('signup-review-viewport'); document.title = title; };
  }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (step !== 'account') document.querySelector('[data-testid="signup-review-heading"]')?.focus({ preventScroll: true });
  }, [step]);
  const accept = data => {
    if (data.signup_token) { saveSignupToken(data.signup_token); setToken(data.signup_token); }
    setProvider(data.provider); setForm({ name: data.name, email: data.email, password: '', business: data.business_name });
    if (data.plan) { setPlanId(data.plan); savePlan(data.plan); }
  };
  const enterActivated = (data, free = false) => {
    localStorage.setItem('token', data.token); auth.setToken(data.token); auth.setUser(data.user);
    setFreeActivated(free); setForm(current => ({ ...current, password: '' })); setStep('success');
    saveSignupToken(''); setToken(''); savePlan(null);
    navigate('/signup', { replace: true });
  };
  const finish = async (sid, capability = token) => {
    setStep('verifying'); setError('');
    // A webhook can be provisioning the same checkout. Retry verification only,
    // never payment confirmation, for this single bounded user interaction.
    let data;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      ({ data } = await paidApi.post('/complete', { signup_token: capability, session_id: sid }));
      if (data.status === 'paid') break;
      if (attempt < 3) await new Promise(resolve => window.setTimeout(resolve, 800));
    }
    if (data.status !== 'paid') { setError('Your payment is still being confirmed. Please check again; don’t pay twice.'); return; }
    enterActivated(data);
  };
  const run = async task => {
    if (gate.current) return;
    gate.current = true; setBusy(true); setError('');
    try { await task(); } catch (err) {
      setError(paidError(err));
      if (err?.response?.status === 401) {
        saveSignupToken(''); setToken(''); setProvider(null); setStep('account');
        setForm(current => ({ ...current, password: '' }));
      }
    }
    finally { gate.current = false; setBusy(false); }
  };
  const loadPlans = async () => { const { data } = await paidApi.get('/plans'); setPlans(data); return data; };
  useEffect(() => {
    if (initial.current) return;
    initial.current = true;
    const boot = async () => {
      try {
        savePlan(planId);
        if (location.pathname === '/signup/google') {
          const sid = new URLSearchParams(location.hash.slice(1)).get('session_id');
          const state = new URLSearchParams(location.search).get('state');
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          if (!sid || !state) throw new Error('incomplete_google');
          const { data } = await paidApi.post('/google/exchange', { session_id: sid, state });
          accept(data); navigate('/signup', { replace: true }); setStep('business');
        } else if (token) {
          const { data } = await paidApi.post('/progress', { signup_token: token }); accept(data);
          const returned = new URLSearchParams(location.search).get('checkout_session');
          if (data.activation_kind === 'free_launchpad' && ['activated', 'activating_free'].includes(data.stage)) {
            const activated = await paidApi.post('/checkout', { signup_token: token, plan: 'launchpad' });
            if (activated.data.payment_required === false && activated.data.token) enterActivated(activated.data, true);
          } else if (returned || data.stage === 'activated') {
            setPayment({ session_id: returned || data.session_id }); await finish(returned || data.session_id, token);
          } else { setStep(data.business_name ? 'plans' : 'business'); }
        } else if (new URLSearchParams(location.search).has('checkout_session')) {
          setError('Sign in if your payment completed, or use the browser where you started signup to continue.');
        }
      } catch (err) {
        const message = err.message === 'incomplete_google' ? 'Google signup was not completed. Please try again.' : paidError(err);
        setError(message);
        if (err?.response?.status === 401) { saveSignupToken(''); setToken(''); setStep('account'); }
        if (location.pathname === '/signup/google') navigate('/signup', { replace: true, state: { paidSignupError: message } });
      } finally { setRestoring(false); }
    };
    boot(); loadPlans().catch(err => setError(paidError(err)));
  }, []);
  const account = () => run(async () => {
    const { data } = token
      ? await paidApi.post('/account', { signup_token: token, name: form.name.trim(), password: form.password || null })
      : await paidApi.post('/start', { name: form.name.trim(), email: form.email.trim(), password: form.password });
    accept(data); setStep('business');
  });
  const business = () => run(async () => {
    const { data } = await paidApi.post('/profile', { signup_token: token, business_name: form.business.trim() });
    accept(data); if (!plans.length) await loadPlans(); setStep('plans');
  });
  const beginPayment = () => run(async () => {
    const { data } = await paidApi.post('/checkout', { signup_token: token, plan: planId }); setPayment(data);
    if (data.payment_required === false && data.token) enterActivated(data, true);
    else if (data.completed) await finish(data.session_id); else setStep('checkout');
  });
  const google = () => run(startPaidGoogle);
  const changeStep = value => { if (!busy) { setError(''); setStep(value); } };
  return { ...auth, step, form, setForm, provider, plans, plan, busy, restoring, error, payment, token, freeActivated,
    account, business, beginPayment, google, changeStep, selectPlan: id => { setPlanId(id); savePlan(id); },
    completePayment: () => run(() => finish(payment.session_id)), retryPlans: () => run(loadPlans),
    enterWorkspace: () => navigate('/onboarding', { replace: true }),
    differentAccount: () => { saveSignupToken(''); savePlan(null); window.location.assign('/signup'); } };
};